@if (alternativeData()) {
  <div class="flex flex-col">
    <!-- The flight's row folds its chips. Folded, it still says what the
         flight becomes, else what it could become. -->
    <button
      type="button"
      class="flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-greyscale-50"
      [attr.aria-expanded]="expanded()"
      [attr.aria-controls]="'alternatives-' + tip().id"
      (click)="expanded.set(!expanded())"
    >
      <span class="flex w-full flex-wrap items-center gap-x-4 gap-y-1">
        <span class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <lucide-icon
            name="plane"
            [size]="16"
            class="shrink-0 text-greyscale-600"
          />
          <span class="text-paragraph-s-reg font-semibold text-primary-900">
            {{ elementLabel() }}
          </span>
          @if (flightRoute()) {
            <span class="text-paragraph-xs-reg tabular-nums text-greyscale-600">
              {{ flightRoute() }}
            </span>
          }
          @if (isEstimate()) {
            <span
              class="flex items-center gap-1 rounded-full border border-warning-200 bg-warning-50 px-2 text-paragraph-xs-reg font-semibold text-warning-700"
              [pTooltip]="'reductionTips.estimate.hint' | transloco"
              tooltipPosition="top"
            >
              <lucide-icon name="info" [size]="12" />
              {{ "reductionTips.estimate.badge" | transloco }}
            </span>
          }
        </span>

        <!-- The flight's one figure: what the chosen chip saves, in kg — the
             chips themselves give the share. -->
        <span class="ml-auto flex items-center gap-2">
          @if (selected()) {
            <span
              class="text-paragraph-s-reg font-semibold tabular-nums text-validation-600"
            >
              −{{ displaySavings() | formatCO2e }}
            </span>
          } @else {
            <span class="text-paragraph-xs-reg text-greyscale-600">
              {{ "reductionTips.flightKept" | transloco }}
            </span>
          }
          <lucide-icon
            name="chevron-down"
            [size]="16"
            class="shrink-0 text-greyscale-600 transition-transform"
            [class.rotate-180]="expanded()"
          />
        </span>
      </span>

      @if (!expanded()) {
        <span
          class="pl-6 text-paragraph-xs-reg tabular-nums text-greyscale-700"
        >
          @if (chosenRow(); as row) {
            {{ "reductionTips.replacedBy" | transloco: { mode: row.label } }}
            @if (row.route; as itinerary) {
              · {{ itinerary.durationHours | durationFormat }} ·
              {{ tipsService.changesLabel(itinerary) }}
            }
          } @else {
            {{
              "reductionTips.alternativesSummary"
                | transloco
                  : {
                      count: modeRows().length,
                      percent: bestSavingsPercent(),
                    }
            }}
          }
        </span>
      }
    </button>

    @if (expanded()) {
      <div
        [id]="'alternatives-' + tip().id"
        class="flex flex-col gap-3 px-4 pb-3"
      >
        <!-- One question per flight: native radios, so arrow keys move
             between the chips, each styled through the label it carries. -->
        <div
          role="radiogroup"
          class="grid grid-cols-2 gap-2 sm:grid-cols-[repeat(auto-fill,minmax(140px,1fr))]"
          [attr.aria-label]="
            'reductionTips.replaceFlight'
              | transloco: { flight: elementLabel() }
          "
        >
          @for (chip of chips(); track chip.value) {
            <div class="relative flex min-w-0">
              <input
                type="radio"
                class="peer sr-only"
                [name]="'alternative-' + tip().id"
                [id]="'alternative-' + tip().id + '-' + chip.value"
                [checked]="chip.isChosen"
                (change)="chip.row ? chooseMode(chip.row.choice) : keepFlight()"
              />
              <label
                [for]="'alternative-' + tip().id + '-' + chip.value"
                class="flex min-h-11 min-w-0 flex-1 cursor-pointer flex-col gap-0.5 rounded-lg border px-3 py-2.5 transition-colors peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary-600"
                [class]="
                  chip.isChosen
                    ? 'border-primary-600 bg-primary-50 ring-1 ring-inset ring-primary-600'
                    : 'border-greyscale-200 bg-white hover:border-primary-300'
                "
              >
                <span
                  class="flex items-center gap-1.5 text-paragraph-s-reg font-semibold text-primary-900"
                >
                  <lucide-icon
                    [name]="chip.icon"
                    [size]="16"
                    class="shrink-0 text-primary-600"
                  />
                  <span class="min-w-0">
                    {{ chip.titleKey | transloco }}
                  </span>
                  <span class="ml-auto flex shrink-0 items-center gap-1">
                    @if (chip.row?.isLowest) {
                      <lucide-icon
                        name="leaf"
                        [size]="14"
                        class="text-validation-600"
                        [pTooltip]="'reductionTips.lowestEmissions' | transloco"
                        tooltipPosition="top"
                      />
                      <span class="sr-only">
                        {{ "reductionTips.lowestEmissions" | transloco }}
                      </span>
                    }
                    @if (chip.isChosen) {
                      <span
                        class="flex h-4 w-4 items-center justify-center rounded-full bg-primary-600 text-white"
                      >
                        <lucide-icon name="check" [size]="10" />
                      </span>
                    }
                  </span>
                </span>
                @if (chip.row; as row) {
                  @if (row.route; as itinerary) {
                    <span
                      class="text-paragraph-xs-reg tabular-nums text-greyscale-700"
                    >
                      {{ itinerary.durationHours | durationFormat }} ·
                      {{ tipsService.changesLabel(itinerary, true) }}
                    </span>
                  }
                  <span
                    class="text-paragraph-xs-reg font-semibold tabular-nums text-validation-600"
                  >
                    −{{ row.savingsPercent }}%
                  </span>
                } @else {
                  <span
                    class="text-paragraph-xs-reg tabular-nums text-greyscale-700"
                  >
                    {{ flightEmissions() | formatCO2e }}
                  </span>
                }
              </label>
            </div>
          }
        </div>

        <!-- The chosen itinerary, step by step. The dialog only switches to
             another itinerary of the same mode. -->
        @if (chosenRow(); as row) {
          <div
            class="flex flex-col gap-2.5 rounded-lg border border-primary-200 bg-primary-50 px-3.5 py-3"
          >
            @if (row.route; as itinerary) {
              <p class="text-paragraph-xs-reg tabular-nums text-greyscale-700">
                <span class="font-semibold text-primary-900">{{
                  row.label
                }}</span>
                · {{ itinerary.durationHours | durationFormat }} ·
                {{ tipsService.changesLabel(itinerary) }}
                @if (row.roadKm) {
                  · {{ row.roadKm }} km
                }
              </p>
              <app-route-legs [route]="itinerary">
                @if (row.routeCount > 1) {
                  <button
                    type="button"
                    class="flex items-center gap-1 font-semibold text-primary-600 hover:underline"
                    (click)="openRoutes(row.choice.mode)"
                  >
                    {{
                      "reductionTips.otherRoutes"
                        | transloco: { count: row.routeCount - 1 }
                    }}
                    <lucide-icon name="chevron-right" [size]="14" />
                  </button>
                }
              </app-route-legs>
            } @else {
              <p
                class="flex items-start gap-2 text-paragraph-xs-reg text-greyscale-700"
              >
                <lucide-icon
                  name="info"
                  [size]="14"
                  class="mt-0.5 shrink-0 text-warning-700"
                />
                <span>
                  <span class="font-semibold text-primary-900">{{
                    row.label
                  }}</span>
                  · {{ "reductionTips.estimate.hint" | transloco }}
                </span>
              </p>
            }
          </div>
        }
      </div>
    }
  </div>
} @else if (selectable()) {
  <div
    class="grid grid-cols-[20px_minmax(0,1fr)] items-start gap-x-3 gap-y-1 px-4 py-3 transition-colors sm:grid-cols-[20px_minmax(0,1fr)_auto]"
    [class.bg-primary-50]="selected()"
  >
    <p-checkbox
      [binary]="true"
      [ngModel]="selected()"
      (onChange)="toggleSelected()"
      [inputId]="'tip-' + tip().id"
      [ariaLabel]="elementLabel() + ' ' + actionLabel()"
    />

    <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      <label
        [for]="'tip-' + tip().id"
        class="cursor-pointer text-paragraph-s-reg font-semibold text-primary-900"
      >
        {{ elementLabel() }}
      </label>
      <span class="text-paragraph-s-reg text-greyscale-800">
        {{ actionLabel() }}
      </span>
    </div>

    <div
      class="col-start-2 flex items-baseline gap-2 tabular-nums sm:col-start-auto sm:flex-col sm:items-end sm:gap-0"
    >
      <span class="text-paragraph-s-reg font-semibold text-validation-600">
        −{{ displaySavings() | formatCO2e }}
      </span>
      <span class="text-paragraph-xs-reg text-validation-600">
        −{{ savingsPercent() }}%
      </span>
    </div>
  </div>
} @else {
  <div class="grid grid-cols-[20px_minmax(0,1fr)] items-start gap-3 px-4 py-3">
    <lucide-icon name="info" [size]="16" class="mt-0.5 text-primary-500" />
    <div class="flex min-w-0 flex-col gap-1.5">
      <p class="text-paragraph-s-reg text-greyscale-800">{{ hint() }}</p>
      @if (durationData(); as duration) {
        <span
          class="flex items-center gap-1.5 text-paragraph-xs-reg text-greyscale-600"
        >
          {{ "impactReport.ecoScore" | transloco }}
          <app-ecoscore [ecoScore]="duration.currentEcoScore" />
        </span>
        <!-- One row per grade gained, the shortest stay reaching it. -->
        <ul
          class="flex flex-col divide-y divide-greyscale-100 rounded-lg border border-greyscale-200"
          [attr.aria-label]="'reductionTips.tripDuration.options' | transloco"
        >
          @for (option of duration.options; track option.days) {
            <li
              class="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-paragraph-xs-reg tabular-nums"
            >
              <span class="flex items-center gap-1.5">
                <lucide-icon
                  name="arrow-right"
                  [size]="14"
                  class="text-greyscale-500"
                />
                <app-ecoscore [ecoScore]="option.ecoScore" />
              </span>
              <span class="font-semibold text-primary-900">
                {{
                  "reductionTips.tripDuration.option.days"
                    | transloco: { days: option.days }
                }}
              </span>
              <span class="text-greyscale-700">
                {{
                  "reductionTips.tripDuration.option.intensity"
                    | transloco: { intensity: (option.intensity | formatCO2e) }
                }}
              </span>
              <span class="ml-auto text-greyscale-600">
                {{
                  "reductionTips.tripDuration.option.extra"
                    | transloco
                      : {
                          extra:
                            (tipsService.toDisplayed(option.additionalImpact)
                            | formatCO2e),
                        }
                }}
              </span>
            </li>
          }
        </ul>
        <!-- Why the footprint grows while the score improves, and why there is
             nothing to tick: a longer stay is set on the trip itself. -->
        <div
          class="flex items-start gap-2 rounded-lg bg-greyscale-50 px-3 py-2.5 text-greyscale-700"
        >
          <lucide-icon
            name="info"
            [size]="14"
            class="mt-0.5 shrink-0 text-primary-500"
          />
          <div class="flex min-w-0 flex-col gap-1">
            <p class="text-paragraph-s-reg">
              <span class="font-semibold text-primary-900">
                {{ "reductionTips.tripDuration.method.title" | transloco }}
              </span>
              {{ "reductionTips.tripDuration.method.text" | transloco }}
            </p>
            <p class="text-paragraph-s-reg">
              {{ "reductionTips.tripDuration.notSelectable" | transloco }}
            </p>
          </div>
        </div>
      }
    </div>
  </div>
}
